import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Upload, Star, Send, Play, Video, X, ImagePlus, Volume2, VolumeX, Share2, Link as LinkIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { flagUrl } from "@/i18n";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useDragCarousel } from "@/lib/useDragCarousel";

// Curated UGC reels — real traders, streamed from Emergent object
// storage via the backend (/api/ugc/video/{name}) so they survive
// production deploys. Order is intentional: the first item autoplays
// on mount, the others are click-to-play.
const UGC_BASE = `${API_BASE}/ugc/video`;
const REAL_UGC = [
  {
    id: "ugc-juan",
    name: "Juan",
    handle: "juan",
    country_code: "ar",
    media_url: `${UGC_BASE}/juan`,
    media_type: "video",
  },
  {
    id: "ugc-caio",
    name: "Caio",
    handle: "caio",
    country_code: "br",
    media_url: `${UGC_BASE}/caio`,
    media_type: "video",
  },
  {
    id: "ugc-hamood",
    name: "Hamood",
    handle: "hamood",
    country_code: "ae",
    media_url: `${UGC_BASE}/hamood`,
    media_type: "video",
  },
  {
    id: "ugc-michael",
    name: "Michael",
    handle: "michael",
    country_code: "us",
    media_url: `${UGC_BASE}/michael`,
    media_type: "video",
  },
  {
    id: "ugc-sofian",
    name: "Sofian",
    handle: "sofian",
    country_code: "fr",
    media_url: `${UGC_BASE}/sofian`,
    media_type: "video",
  },
  {
    id: "ugc-nathalie",
    name: "Nathalie",
    handle: "nathalie",
    country_code: "de",
    media_url: `${UGC_BASE}/nathalie`,
    media_type: "video",
  },
];

// 9:16 vertical dimension check helper
function check9x16(width, height, tolerance = 0.06) {
  if (!width || !height) return false;
  const ratio = width / height;
  return Math.abs(ratio - 9 / 16) <= tolerance;
}

/* Brand glyphs (lucide has no WhatsApp / X / Telegram marks). */
const WhatsAppIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
const XIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const TelegramIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

/* Individual reel player used inside the UGC carousel. The first
   reel on the page (autoPlay=true) starts muted+looped as soon as
   it enters the DOM — subsequent reels wait for a user click. Once
   any reel is playing the centred Play badge fades out. */
function ReelPlayer({ it, i, autoPlay }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!autoPlay || !videoRef.current) return;
    const el = videoRef.current;
    el.muted = true;
    const p = el.play();
    if (p && typeof p.then === "function") {
      p.then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, [autoPlay]);

  const toggle = (e) => {
    e.stopPropagation();
    if (shareOpen) {
      setShareOpen(false);
      return;
    }
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const shareUrl = `${window.location.origin}/?reel=${it.handle}`;
  const doShare = (e, target) => {
    e.stopPropagation();
    const u = encodeURIComponent(shareUrl);
    if (target === "copy") {
      navigator.clipboard?.writeText(shareUrl)
        .then(() => toast.success(t("ugc.linkCopied", "Link copied!")))
        .catch(() => toast.error(shareUrl));
    } else {
      const links = {
        whatsapp: `https://wa.me/?text=${u}`,
        x: `https://twitter.com/intent/tweet?url=${u}`,
        telegram: `https://t.me/share/url?url=${u}`,
      };
      window.open(links[target], "_blank", "noopener,noreferrer");
    }
    setShareOpen(false);
  };

  return (
    <div
      data-testid={`ugc-reel-${i}`}
      data-ugc-card="1"
      className="shrink-0 relative rounded-2xl overflow-hidden group cursor-pointer"
      style={{
        width: "clamp(190px, 22vw, 320px)",
        aspectRatio: "9 / 16",
        border: "1px solid color-mix(in srgb, var(--brand) 18%, transparent)",
        scrollSnapAlign: "center",
        willChange: "transform",
      }}
      onClick={toggle}
    >
      {it.media_url ? (
        it.media_type === "video" ? (
          <video
            ref={videoRef}
            src={it.media_url}
            className="absolute inset-0 w-full h-full object-cover"
            muted
            playsInline
            loop
            preload={autoPlay ? "auto" : "metadata"}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onError={() => setPlaying(false)}
          />
        ) : (
          <img src={it.media_url} alt={it.name} className="absolute inset-0 w-full h-full object-cover" />
        )
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(${135 + i * 30}deg,
              color-mix(in srgb, var(--brand) 28%, var(--surface)) 0%,
              color-mix(in srgb, var(--brand-deep) 38%, var(--surface-2)) 50%,
              var(--surface-2) 100%)`,
          }}
        />
      )}

      {/* Bottom gradient overlay for label readability */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
           style={{ background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)" }} />

      {/* Play badge — hidden while playing */}
      <AnimatePresence>
        {!playing && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 grid place-items-center pointer-events-none"
          >
            <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-white/95 grid place-items-center shadow-2xl group-hover:scale-110 transition-transform">
              <Play size={22} className="text-black ml-1" fill="black" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mute toggle — only visible while playing */}
      {playing && it.media_type === "video" && (
        <button
          type="button"
          onClick={toggleMute}
          data-testid={`ugc-mute-${i}`}
          aria-label={muted ? t("ugc.unmute", "Unmute") : t("ugc.mute", "Mute")}
          className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-black/60 backdrop-blur grid place-items-center text-white hover:bg-black/80 transition-colors"
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
      )}

      {/* Share — icon-only menu (WhatsApp / X / Telegram / copy link) */}
      <div className="absolute top-2.5 left-2.5 flex flex-col items-center gap-1.5">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShareOpen((s) => !s); }}
          data-testid={`ugc-share-${i}`}
          aria-label={t("ugc.share", "Share")}
          className="w-8 h-8 rounded-full bg-black/60 backdrop-blur grid place-items-center text-white hover:bg-black/80 transition-colors"
        >
          <Share2 size={14} />
        </button>
        <AnimatePresence>
          {shareOpen && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" onClick={(e) => doShare(e, "whatsapp")} data-testid={`ugc-share-whatsapp-${i}`}
                aria-label="WhatsApp"
                className="w-8 h-8 rounded-full bg-black/60 backdrop-blur grid place-items-center text-white hover:bg-[#25D366] transition-colors">
                <WhatsAppIcon />
              </button>
              <button type="button" onClick={(e) => doShare(e, "x")} data-testid={`ugc-share-x-${i}`}
                aria-label="X"
                className="w-8 h-8 rounded-full bg-black/60 backdrop-blur grid place-items-center text-white hover:bg-black transition-colors">
                <XIcon />
              </button>
              <button type="button" onClick={(e) => doShare(e, "telegram")} data-testid={`ugc-share-telegram-${i}`}
                aria-label="Telegram"
                className="w-8 h-8 rounded-full bg-black/60 backdrop-blur grid place-items-center text-white hover:bg-[#229ED9] transition-colors">
                <TelegramIcon />
              </button>
              <button type="button" onClick={(e) => doShare(e, "copy")} data-testid={`ugc-share-copy-${i}`}
                aria-label={t("ugc.copyLink", "Copy link")}
                className="w-8 h-8 rounded-full bg-black/60 backdrop-blur grid place-items-center text-white hover:bg-brand transition-colors">
                <LinkIcon size={13} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom label: name + flag */}
      <div className="absolute inset-x-3 bottom-3 flex items-end justify-between gap-2 pointer-events-none">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-white font-display text-sm lg:text-base font-medium truncate">
            <span className="truncate">{it.name?.split(" ")[0] || "@trader"}</span>
            {it.country_code && (
              <img src={flagUrl(it.country_code)} alt={it.country_code}
                   className="w-5 h-auto rounded-[2px] shrink-0" />
            )}
          </div>
          <div className="text-brand text-[10px] uppercase tracking-widest font-semibold">
            @{(it.handle || it.name || "trader").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 12)}
          </div>
        </div>
      </div>
    </div>
  );
}


function ShareModal({ open, onClose, t }) {
  const [form, setForm] = useState({ name: "", handle: "", country: "", content: "", rating: 5 });
  const [media, setMedia] = useState({ url: "", type: "image" });
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8 MB)");
      return;
    }
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) {
      toast.error("Only image or short video files are accepted");
      return;
    }
    // Validate 9:16 dimensions
    const url = URL.createObjectURL(file);
    const ok = await new Promise((resolve) => {
      if (isImage) {
        const img = new Image();
        img.onload = () => resolve(check9x16(img.naturalWidth, img.naturalHeight));
        img.onerror = () => resolve(false);
        img.src = url;
      } else {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => resolve(check9x16(v.videoWidth, v.videoHeight));
        v.onerror = () => resolve(false);
        v.src = url;
      }
    });
    if (!ok) {
      URL.revokeObjectURL(url);
      toast.error("Media must be 9:16 vertical aspect ratio");
      return;
    }
    // Convert to dataURL for upload (lightweight; admin can re-host later)
    const reader = new FileReader();
    reader.onload = () => {
      setMedia({ url: reader.result, type: isImage ? "image" : "video" });
      URL.revokeObjectURL(url);
      toast.success("Media accepted — 9:16 ✓");
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.content.trim()) {
      toast.error("Name and story are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/ugc", {
        ...form,
        media_url: media.url || null,
        media_type: media.url ? media.type : null,
        rating: form.rating ? Number(form.rating) : null,
      });
      toast.success("Submitted! Your story is pending review.");
      setForm({ name: "", handle: "", country: "", content: "", rating: 5 });
      setMedia({ url: "", type: "image" });
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur grid place-items-center p-4"
          onClick={onClose}
          data-testid="ugc-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="relative card-beam p-6 lg:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="ugc-modal"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="ugc-modal-close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[color:var(--surface-2)] border border-[color:var(--line)] grid place-items-center text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
            >
              <X size={14} />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Upload className="text-brand" size={18} />
              <h3 className="font-display text-lg font-medium text-[color:var(--text-1)]">{t("ugc.shareTitle")}</h3>
            </div>
            <p className="text-[color:var(--text-2)] text-sm mb-5 font-light">{t("ugc.shareSubtitle")}</p>

            <form onSubmit={submit} className="space-y-3" data-testid="ugc-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="field-wrap">
                  <input data-testid="ugc-name" required placeholder={t("ugc.yourName")} value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="field-wrap">
                  <input data-testid="ugc-handle" placeholder={t("ugc.handle")} value={form.handle}
                    onChange={(e) => setForm({ ...form, handle: e.target.value })} />
                </div>
              </div>
              <div className="field-wrap">
                <input data-testid="ugc-country" placeholder={t("ugc.country")} value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
              <div className="field-wrap">
                <textarea data-testid="ugc-content" required rows={3} placeholder={t("ugc.story")}
                  value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
              </div>

              {/* 9:16 vertical file upload zone */}
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                data-testid="ugc-upload-zone"
                className="relative border-2 border-dashed border-[color:var(--line-strong)] hover:border-brand rounded-xl overflow-hidden cursor-pointer transition-colors mx-auto"
                style={{ aspectRatio: "9 / 16", maxWidth: 240 }}
              >
                {media.url ? (
                  media.type === "video"
                    ? <video src={media.url} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
                    : <img src={media.url} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-center p-4">
                    <div>
                      <ImagePlus className="mx-auto text-brand mb-2" size={28} />
                      <div className="text-sm text-[color:var(--text-1)] font-medium">Click or drop a 9:16 file</div>
                      <div className="text-xs text-[color:var(--text-3)] font-mono mt-1">vertical reel · max 8MB · strict 9:16</div>
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="ugc-file-input"
                />
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}
                      data-testid={`ugc-rating-${n}`} className="p-1">
                      <Star size={16} className={n <= form.rating ? "fill-brand text-brand" : "text-zinc-700"} />
                    </button>
                  ))}
                </div>
                <button type="submit" data-testid="ugc-submit" disabled={submitting}
                  className="btn-solid-brand text-sm disabled:opacity-50">
                  <Send size={14} /> {submitting ? t("ugc.submit") + "…" : t("ugc.submit")}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function UGCSection() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [sharedReel] = useState(() => new URLSearchParams(window.location.search).get("reel"));

  useEffect(() => {
    api.get("/ugc").then(({ data }) => {
      const withMedia = data.filter((u) => u.media_url);
      setItems(withMedia);
    }).catch((err) => {
      if (process.env.NODE_ENV === "development") console.debug("UGC fetch failed", err);
    });
  }, []);

  // We show the 4 curated UGC reels. If admin approves more reels
  // via the /ugc endpoint they will replace the curated list.
  const reels = items.length ? items.slice(0, 9) : REAL_UGC;

  /* Drag-to-scroll with momentum — pointer events handle both mouse
     and touch. Implementation extracted to `useDragCarousel` so the
     carousel mechanics can be reused (and easily unit-tested) by any
     future scroll-strip on the site. */
  const { stripRef, onPointerDown, onPointerMove, endDrag, onClickCapture } = useDragCarousel();

  // Center the middle reel on mount ONLY when the strip overflows
  // (i.e. more than ~4 curated reels). With a short list `justify-
  // content: safe center` already handles centering; forcing a scroll
  // would shift the row off-centre. Runs again on resize.
  useEffect(() => {
    if (!stripRef.current || !reels.length) return;
    const center = () => {
      const strip = stripRef.current;
      if (!strip) return;
      // Nothing to scroll — flex centering already puts the row in place.
      if (strip.scrollWidth <= strip.clientWidth + 4) return;
      const cards = strip.querySelectorAll('[data-ugc-card="1"]');
      if (!cards.length) return;
      const middle = cards[Math.floor(cards.length / 2)];
      if (!middle) return;
      const stripRect = strip.getBoundingClientRect();
      const cardRect = middle.getBoundingClientRect();
      const currentLeft = strip.scrollLeft;
      const offset =
        cardRect.left - stripRect.left + cardRect.width / 2 - stripRect.width / 2;
      strip.style.scrollSnapType = "none";
      strip.scrollTo({ left: currentLeft + offset, behavior: "instant" in window ? "instant" : "auto" });
      requestAnimationFrame(() => {
        strip.style.scrollSnapType = "x mandatory";
      });
    };
    const id = setTimeout(center, 50);
    window.addEventListener("resize", center);
    return () => {
      clearTimeout(id);
      window.removeEventListener("resize", center);
    };
  }, [reels.length]);

  // Shared-link deep-linking: /?reel={handle} scrolls the UGC section
  // into view and autoplays that specific reel.
  useEffect(() => {
    if (!sharedReel || !reels.some((r) => r.handle === sharedReel)) return;
    const id = setTimeout(() => {
      document.getElementById("ugc")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    return () => clearTimeout(id);
  }, [sharedReel, reels]);

  return (
    <section id="ugc" data-testid="ugc-section" className="relative py-14 lg:py-28 overflow-hidden">
      {/* Header sits in normal max-width container */}
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 mb-10 lg:mb-14 text-center">
        <div className="pill-mono inline-flex mb-4"><Video size={11} className="mr-1.5 -ml-1" /> {t("ugc.pill")}</div>
        <h2 className="font-display text-3xl lg:text-5xl font-medium tracking-tight mb-3">
          {t("ugc.title")} <span className="text-gradient-brand font-semibold">{t("ugc.titleAccent")}</span>
        </h2>
        <p className="text-[color:var(--text-2)] max-w-2xl mx-auto text-sm lg:text-base font-light">
          {t("ugc.subtitle")}
        </p>
      </div>

      {/* FULL-BLEED horizontal reel strip — overflows the page on both
          sides, exactly like the Moneta reference the user shared.
          Native overflow-x for touch + custom mouse-drag for desktop. */}
      <div
        className="relative w-full overflow-hidden"
        data-testid="ugc-strip"
        style={{
          maskImage:
            "linear-gradient(90deg, transparent 0%, #000 5%, #000 95%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(90deg, transparent 0%, #000 5%, #000 95%, transparent 100%)",
        }}
      >
        <div
          ref={stripRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={endDrag}
          onClickCapture={onClickCapture}
          className="flex gap-3 sm:gap-4 lg:gap-5 overflow-x-auto pb-6 scrollbar-hidden select-none justify-start lg:justify-center"
          style={{
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-x",
            scrollBehavior: "auto",
            // On desktop with a small curated list, `justify-center` centres
            // the row. When more reels are added and the row overflows,
            // `safe` lets the browser fall back to flex-start so the first
            // card is still reachable via scroll. On mobile we always
            // scroll horizontally, so keep flex-start with side padding.
            justifyContent: "safe center",
            paddingLeft: "clamp(20px, 4vw, 80px)",
            paddingRight: "clamp(20px, 4vw, 80px)",
            cursor: "grab",
          }}
        >
          {reels.map((it, i) => (
            <ReelPlayer
              key={it.id}
              it={it}
              i={i}
              autoPlay={sharedReel && reels.some((r) => r.handle === sharedReel) ? it.handle === sharedReel : i === 0}
            />
          ))}
        </div>
      </div>

      {/* Thin "Share Your Story" CTA bar */}
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 mt-10">
        <div
          className="card-beam card-beam-glow flex flex-col sm:flex-row items-center justify-between gap-4 p-4 lg:p-5"
          data-testid="ugc-share-bar"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-brand/10 border border-brand/30 grid place-items-center shrink-0">
              <Upload className="text-brand" size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-sm lg:text-base font-medium text-[color:var(--text-1)] leading-tight">
                {t("ugc.shareTitle")}
              </div>
              <div className="text-[color:var(--text-3)] text-xs truncate">
                Upload a 9:16 vertical reel · admin approval
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="ugc-open-modal"
            className="btn-solid-brand text-sm shrink-0"
          >
            <Send size={14} /> {t("ugc.shareTitle")}
          </button>
        </div>
      </div>

      <ShareModal open={open} onClose={() => setOpen(false)} t={t} />
    </section>
  );
}
